<template>
  <div class="payment-summary">
    <el-row>
      <p class="title">{{ $t('plan_payment.payment_summary.title') }}</p>
    </el-row>
    <el-row>
      <el-col :span="24">
        <el-row>
          <p class="sub-title">{{ $t('plan_payment.payment_summary.sub_title') }}</p>
        </el-row>
        <el-row type="flex" justify="space-between">
          <p class="product-title">{{ product.label }}</p>
          <p class="price">
            {{ currencyFormat(product.price) }}
          </p>
        </el-row>
        <el-row>
          <p class="price-per-month" v-if="product.interval != 'lifetime'">
            {{ currencyFormat(product.pricePerMonth) }} {{ $t('plan_payment.payment_summary.user_month') }}
          </p>
        </el-row>
        <el-row type="flex" justify="space-between">
          <p class="number-of-users">{{ $t('plan_payment.payment_summary.number_of_users') }}</p>
          <p class="quantity">x {{ quantity }}</p>
        </el-row>
        <hr>
        <el-row type="flex" justify="space-between" style="align-items: baseline">
          <el-col :span="12">
            <p class="total">
              {{ $t('plan_payment.payment_summary.total') }}
            </p>
          </el-col>
          <el-col :span="12">
            <p class="total-price text-align-right">
              {{ currencyFormat(amount) }}
            </p>
            <p class="save-amount text-align-right">
              {{ $t('plan_payment.payment_summary.you_save', [currencyFormat(savingAmount)]) }}
            </p>
          </el-col>
        </el-row>
        <hr>
        <el-row>
          <p v-if="isSubscription" class="term">
            {{ $t('plan_payment.payment_summary.next_billing_date') }} <b>{{ nextBillingDate }}.</b> {{ $t('plan_payment.payment_summary.can_cancel') }}
          </p>
        </el-row>
        <el-row type="flex" justify="center">
          <primary-button
            v-if="product.interface"
            type="submit"
            class="bigger"
            @click.native="handleSubscribe"
            v-loading.fullscreen.lock="loading"
          >
            {{ product.interface.button_text }}
          </primary-button>
        </el-row>
        <el-row>
          <el-col :span="24">
            <p class="footer-term">
              <a href="https://elsaspeak.com/en/english-for-companies/homepage#basic">
                {{ $t('plan_payment.payment_summary.footer_term_1') }}</a> {{ $t('plan_payment.payment_summary.footer_term_2') }}
            </p>
          </el-col>
        </el-row>
      </el-col>
    </el-row>
  </div>
</template>

<script>
  import { INTERVAL } from '@/constants/generic';
  import PrimaryButton from "@/components/common/buttons/PrimaryButton";


  export default {
    components: {
      PrimaryButton
    },
    props: {
      product: {
        type: Object,
        default: function() {
          return {
            label: '',
            price: 0,
            base_price: 0,
            pricePerMonth: 0,
            trial_period_days: 0,
            interval: INTERVAL.ONE_YEAR.key,
            currency: 'usd'
          };
        }
      },
      quantity: {
        type: Number,
        default: 5
      },
      locale: {
        type: String,
        default: 'en-US'
      },
      loading: {
        type: Boolean,
        default: false
      }
    },
    data: () => ({}),
    computed: {
      amount () {
        return this.product.price * this.quantity;
      },
      savingAmount () {
        return (this.product.base_price * this.quantity) - this.amount;
      },
      nextBillingDate () {
        const year = new Date().getFullYear();
        const month = new Date().getMonth();
        const day = new Date().getDate();
        let date = new Date(year, month, day);
        const shortMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        if (this.product.trial_period_days > 0) {
          date = new Date(year, month, day + this.product.trial_period_days);
        } else if (this.product.interval === INTERVAL.ONE_YEAR.key) {
          date = new Date(year + 1, month, day);
        } else if (this.product.interval === INTERVAL.SIX_MONTHS.key) {
          date = new Date(year, month + INTERVAL.SIX_MONTHS.value, day);
        } else if (this.product.interval === INTERVAL.THREE_MONTHS.key) {
          date = new Date(year, month + INTERVAL.THREE_MONTHS.value, day);
        }
        return date.getDate() + 'th ' + shortMonths[date.getMonth()] + ' ' + date.getFullYear();
      },
      isSubscription () {
        return this.product.type === 'subscription';
      }
    },
    methods: {
      currencyFormat (number) {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.product.currency.toUpperCase()
        }).format(number);
      },
      handleSubscribe () {
        this.$emit('subscribe');
      }
    }
  };
</script>

<style lang="scss" scoped>
  p {
    font-size: 18px;
  }

  .title {
    font-size: 24px;
    line-height: 120%;
    font-weight: bold;
    color: #333333;
    letter-spacing: unset;
  }

  .sub-title {
    line-height: 185%;
    font-weight: bold;
    color: #333333;
  }

  h1 {
    font-weight: normal;
    font-size: 24px;
    line-height: 120%;
    color: #4294FF;
  }

  .product-title {
    font-size: 20px;
    line-height: 30px;
    font-weight: 500;
    font-style: normal;
    padding: 0;
    margin: 0;
    color: #478DFF;
  }

  .price {
    font-size: 20px;
    line-height: 30px;
    /* display: contents; */
    padding: 0;
    margin: 0;
  }

  .total-price {
    font-weight: bold;
    font-size: 24px;
    line-height: 120%;
    color: #333333;
  }

  .text-align-right {
    text-align: right;
  }

  .term {
    font-style: italic;
    font-weight: normal;
    font-size: 14px;
    line-height: 120%;
    color: #33333399;
  }

  .footer-term {
    font-weight: normal;
    line-height: 185%;
    color: #333333;

    a {
      font-size: 18px;
      line-height: 185%;
      color: #4294FF;
    }
  }

  hr {
    opacity: 0.1;
    border: 1px solid #000000;
  }

  .el-button {
    width: 260px;
    height: 56px;

    ::v-deep span {
      font-weight: bold;
      font-size: 20px;
      line-height: 24px;
      letter-spacing: 0.005em;
    }
  }
</style>
